/**
 * MessagingCenterV2 - Centre de Messagerie Professionnel (Refonte UX)
 * ===================================================================
 * Structure 3 panneaux :
 * - Gauche : Liste des conversations avec filtres avancés
 * - Centre : Fil de discussion
 * - Droite : Contexte (profil contact + opération liée)
 * 
 * Adapté à un usage B2B opérationnel (support + logistique)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Search, Send, Paperclip, MoreVertical, Phone, Mail, Building2,
  Check, CheckCheck, Clock, FileText, Bell, Filter, SortDesc,
  MessageSquare, Package, ArrowLeft, X, RefreshCw, ChevronDown,
  Circle, Truck, User, MapPin, Calendar, CreditCard, UserCheck,
  AlertCircle, Inbox, PanelRightClose, PanelRightOpen, ExternalLink,
  FileEdit, StickyNote, RotateCcw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { ResponseTemplatesModal, InternalNotes, PrioritySelector, PriorityBadge, PRIORITY_LEVELS } from '../components/messaging';
import { formatVehicleType } from '../utils/formatters';
import { useChatWebSocket } from '../hooks/useChatWebSocket';

const token = 'cookie-auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// UTILITIES
// ============================================================

const formatTimestamp = (date) => {
  if (!date) return '';
  const now = new Date();
  const msgDate = new Date(date);
  const diffMs = now - msgDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) {
    return msgDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return msgDate.toLocaleDateString('fr-FR', { weekday: 'short' });
  return msgDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const formatFullDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const formatMessageTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getStatusLabel = (status) => {
  const labels = {
    'OPEN': 'Ouverte',
    'RESOLVED': 'Résolue',
    'PENDING': 'En attente',
    'CLOSED': 'Fermée'
  };
  return labels[status] || status;
};

const getStatusColor = (status) => {
  const colors = {
    'OPEN': 'bg-blue-100 text-blue-700',
    'RESOLVED': 'bg-green-100 text-green-700',
    'PENDING': 'bg-yellow-100 text-yellow-700',
    'CLOSED': 'bg-gray-100 text-gray-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

const getOperationStatusLabel = (status) => {
  const labels = {
    'demande_enregistree': 'Enregistrée',
    'en_attente_de_devis': 'Attente devis',
    'en_attente_validation_client': 'Attente validation',
    'en_negociation': 'Négociation',
    'devis_accepte': 'Devis accepté',
    'planifiee': 'Planifiée',
    'prete_au_chargement': 'Prête chargement',
    'en_cours_de_chargement': 'Chargement',
    'charge_en_route': 'En route',
    'livree': 'Livrée',
    'en_attente_paiement': 'Attente paiement',
    'payee': 'Payée',
    'terminee': 'Terminée',
    'annulee_admin': 'Annulée',
    'annulee_par_client': 'Annulée',
    'cancelled': 'Annulée',
    'pending': 'En attente',
    'accepted': 'Accepté',
    'in_progress': 'En cours',
    'completed': 'Terminé'
  };
  return labels[status] || status;
};

// ============================================================
// CONVERSATION LIST ITEM
// ============================================================

const ConversationItem = ({ conversation, isSelected, onClick, currentUserId }) => {
  const hasUnread = (conversation.unreadCountInternal || 0) > 0;
  const unreadCount = conversation.unreadCountInternal || 0;
  const isAssignedToMe = conversation.assignedAgentId === currentUserId;
  
  const avatarColors = [
    'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
    'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500'
  ];
  const colorIndex = (conversation.clientName || '').charCodeAt(0) % avatarColors.length;

  return (
    <div
      onClick={onClick}
      data-testid={`conversation-item-${conversation.id}`}
      className={`
        p-3 cursor-pointer transition-all duration-150 border-b border-gray-100
        ${isSelected 
          ? 'bg-emerald-50 border-l-4 border-l-emerald-500' 
          : hasUnread
            ? 'bg-emerald-50/50 hover:bg-emerald-50 border-l-4 border-l-emerald-400'
            : 'bg-white hover:bg-gray-50 border-l-4 border-l-transparent'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <Avatar className={`h-10 w-10 ${avatarColors[colorIndex]}`}>
            <AvatarFallback className="text-white font-medium text-sm">
              {getInitials(conversation.clientName)}
            </AvatarFallback>
          </Avatar>
          {hasUnread && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white text-xs font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center justify-between mb-1">
            <span className={`truncate text-sm ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
              {conversation.clientName || 'Client'}
            </span>
            <span className={`text-xs flex-shrink-0 ml-2 ${hasUnread ? 'text-emerald-600 font-semibold' : 'text-gray-400'}`}>
              {formatTimestamp(conversation.lastMessageAt)}
            </span>
          </div>

          {/* Operation Reference */}
          {conversation.type === 'OPERATION' && conversation.operationReference && (
            <div className="flex items-center gap-1.5 mb-1">
              <Package className="w-3 h-3 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-blue-600 font-medium truncate">
                {conversation.operationReference}
              </span>
              {conversation.operationDeparture && conversation.operationArrival && (
                <span className="text-xs text-gray-400 truncate">
                  • {conversation.operationDeparture?.split(',')[0]} → {conversation.operationArrival?.split(',')[0]}
                </span>
              )}
            </div>
          )}

          {/* Last Message Preview */}
          <p className={`text-sm truncate ${hasUnread ? 'text-gray-800' : 'text-gray-500'}`}>
            {conversation.lastMessagePreview || 'Aucun message'}
          </p>

          {/* Footer Row: Status + Assignment */}
          <div className="flex items-center gap-2 mt-1.5">
            <Badge className={`text-[10px] px-1.5 py-0 h-4 ${getStatusColor(conversation.status)}`}>
              {getStatusLabel(conversation.status)}
            </Badge>
            {isAssignedToMe && (
              <Badge className="text-[10px] px-1.5 py-0 h-4 bg-purple-100 text-purple-700">
                <UserCheck className="w-2.5 h-2.5 mr-0.5" />
                Moi
              </Badge>
            )}
            {conversation.assignedAgentName && !isAssignedToMe && (
              <span className="text-[10px] text-gray-400 truncate">
                {conversation.assignedAgentName}
              </span>
            )}
            {!conversation.assignedAgentId && (
              <span className="text-[10px] text-orange-500 font-medium">Non assigné</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CONVERSATION LIST SKELETON
// ============================================================

const ConversationSkeleton = () => (
  <div className="p-3 border-b border-gray-100">
    <div className="flex items-start gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="flex justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-3 w-full" />
      </div>
    </div>
  </div>
);

// ============================================================
// MESSAGE BUBBLE
// ============================================================

const MessageBubble = ({ message, isOwn, showDate, currentUserId, onRetry }) => {
  const isRead = message.readBy?.includes(currentUserId) || message.isRead;

  // Derive 4 sending states: sending | sent | read | error
  const status = (() => {
    if (message.error || message.status === 'error') return 'error';
    if (message.sending || message.status === 'sending') return 'sending';
    if (isRead) return 'read';
    if (message.status === 'delivered') return 'delivered';
    return 'sent';
  })();

  const renderAttachment = (attachment) => {
    const isImage = attachment.fileType === 'image' || 
      /\.(jpg|jpeg|png|gif|webp)$/i.test(attachment.filename);
    
    if (isImage) {
      return (
        <div className="mt-2 rounded-lg overflow-hidden max-w-[240px]">
          <img 
            src={attachment.url || `${API}${attachment.path}`} 
            alt={attachment.filename}
            className="w-full h-auto"
          />
        </div>
      );
    }
    
    return (
      <a 
        href={attachment.url || `${API}${attachment.path}`}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-2 flex items-center gap-2 p-2 rounded-lg ${isOwn ? 'bg-white/20' : 'bg-gray-100'}`}
      >
        <FileText className="w-6 h-6 text-gray-500" />
        <span className="text-sm truncate">{attachment.filename}</span>
      </a>
    );
  };

  const StatusIcon = () => {
    switch (status) {
      case 'sending':
        return <Clock className="w-3.5 h-3.5 text-white/60" aria-label="Envoi en cours" />;
      case 'sent':
        return <Check className="w-3.5 h-3.5 text-white/60" aria-label="Envoyé" />;
      case 'delivered':
        return <CheckCheck className="w-3.5 h-3.5 text-white/70" aria-label="Livré" />;
      case 'read':
        return <CheckCheck className="w-3.5 h-3.5 text-blue-300" aria-label="Lu" />;
      case 'error':
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRetry && onRetry(message); }}
            className="flex items-center gap-1 text-red-300 hover:text-red-200 text-[10px] font-semibold ml-1"
            data-testid={`retry-msg-${message.id || message._id || 'tmp'}`}
            aria-label="Erreur d'envoi, cliquer pour réessayer"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            <RotateCcw className="w-3 h-3" />
            <span>Réessayer</span>
          </button>
        );
      default:
        return null;
    }
  };

  const errorRing = status === 'error' ? 'ring-1 ring-red-300' : '';

  return (
    <>
      {showDate && (
        <div className="flex items-center justify-center my-4" data-testid="date-separator">
          <span className="px-3 py-1 bg-white border border-zinc-100 rounded-full text-[11px] font-medium text-zinc-500 shadow-sm">
            {formatFullDate(message.createdAt)}
          </span>
        </div>
      )}
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
        <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
          {!isOwn && message.senderName && (
            <p className="text-xs text-gray-500 mb-1 ml-1">{message.senderName}</p>
          )}
          <div className={`
            px-4 py-2 rounded-2xl ${errorRing}
            ${isOwn 
              ? 'bg-emerald-500 text-white rounded-br-md' 
              : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
            }
            ${status === 'sending' ? 'opacity-80' : ''}
          `}>
            {message.content && (
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
            )}
            {message.attachments?.map((att, idx) => (
              <div key={idx}>{renderAttachment(att)}</div>
            ))}
            <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
              <span className={`text-[10px] ${isOwn ? 'text-white/70' : 'text-gray-400'}`}>
                {formatMessageTime(message.createdAt)}
              </span>
              {isOwn && <StatusIcon />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================
// CONTEXT PANEL (RIGHT)
// ============================================================

const ContextPanel = ({ conversation, operation, client, onClose }) => {
  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center text-gray-400">
        <div>
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Sélectionnez une conversation pour voir le contexte</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Contexte</h3>
        <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Contact Info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact</h4>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 bg-emerald-500">
                  <AvatarFallback className="text-white">
                    {getInitials(conversation.clientName)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-gray-900">{conversation.clientName || 'Client'}</p>
                  <p className="text-xs text-gray-500">Client</p>
                </div>
              </div>
              {client?.email && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="truncate">{client.email}</span>
                </div>
              )}
              {client?.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{client.phone}</span>
                </div>
              )}
              {client?.company && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span>{client.company}</span>
                </div>
              )}
            </div>
          </div>

          {/* Operation Info */}
          {conversation.type === 'OPERATION' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Opération liée</h4>
              <div className="bg-blue-50 rounded-lg p-3 space-y-3">
                {/* Operation Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-blue-700">
                      {conversation.operationReference || operation?.operationNumber || 'N/A'}
                    </span>
                  </div>
                  {(conversation.operationStatus || operation?.status) && (
                    <Badge className="text-xs bg-blue-100 text-blue-700">
                      {getOperationStatusLabel(conversation.operationStatus || operation?.status)}
                    </Badge>
                  )}
                </div>

                {/* Route */}
                {(conversation.operationDeparture || operation?.departure) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-gray-700">{conversation.operationDeparture || operation?.departure}</p>
                      <p className="text-gray-400">↓</p>
                      <p className="text-gray-700">{conversation.operationArrival || operation?.arrival}</p>
                    </div>
                  </div>
                )}

                {/* Additional Operation Details */}
                {operation && (
                  <>
                    {operation.preferredDate && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {new Date(operation.preferredDate).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                    {operation.vehicleType && (
                      <div className="flex items-center gap-2 text-sm">
                        <Truck className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{formatVehicleType(operation.vehicleType)}</span>
                      </div>
                    )}
                    {operation.weight && (
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{operation.weight} tonnes</span>
                      </div>
                    )}
                    {operation.finalQuote && (
                      <div className="flex items-center gap-2 text-sm">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 font-medium">
                          {operation.finalQuote.toLocaleString()} XOF
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Link to Operation */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => window.open(`/admin/operations/${conversation.operationId}`, '_blank')}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  Voir l&apos;opération
                </Button>
              </div>
            </div>
          )}

          {/* Assignment */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Assignation</h4>
            <div className="bg-gray-50 rounded-lg p-3">
              {conversation.assignedAgentName ? (
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-gray-700">{conversation.assignedAgentName}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Non assigné</span>
                </div>
              )}
            </div>
          </div>

          {/* Conversation Info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Conversation</h4>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Créée le</span>
                <span className="text-gray-700">{formatFullDate(conversation.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="text-gray-700">{conversation.type === 'OPERATION' ? 'Opération' : 'Direct'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Statut</span>
                <Badge className={`text-xs ${getStatusColor(conversation.status)}`}>
                  {getStatusLabel(conversation.status)}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

// ============================================================
// EMPTY STATES
// ============================================================

const EmptyConversationList = ({ onRefresh }) => (
  <div className="h-full flex items-center justify-center p-6">
    <div className="text-center">
      <Inbox className="w-16 h-16 mx-auto mb-4 text-gray-300" />
      <h3 className="text-lg font-medium text-gray-700 mb-2">Aucune conversation</h3>
      <p className="text-sm text-gray-500 mb-4">
        Les conversations avec vos clients apparaîtront ici
      </p>
      <Button variant="outline" onClick={onRefresh}>
        <RefreshCw className="w-4 h-4 mr-2" />
        Rafraîchir
      </Button>
    </div>
  </div>
);

const EmptyChat = () => (
  <div className="h-full flex items-center justify-center p-6 bg-gray-50">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
        <MessageSquare className="w-10 h-10 text-emerald-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">
        Centre de Messagerie
      </h3>
      <p className="text-gray-500 mb-6">
        Sélectionnez une conversation dans la liste pour commencer à échanger avec vos clients.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Badge variant="outline" className="text-xs">
          <Circle className="w-2 h-2 mr-1 fill-emerald-500 text-emerald-500" />
          Non lues en premier
        </Badge>
        <Badge variant="outline" className="text-xs">
          <Filter className="w-3 h-3 mr-1" />
          Filtres avancés
        </Badge>
        <Badge variant="outline" className="text-xs">
          <Package className="w-3 h-3 mr-1" />
          Contexte opération
        </Badge>
      </div>
    </div>
  </div>
);

// ============================================================
// MESSAGE INPUT
// ============================================================

const MessageInput = ({ onSend, disabled, onOpenTemplates, onTyping }) => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if ((!message.trim() && files.length === 0) || sending || disabled) return;

    setSending(true);
    onTyping?.(false);
    try {
      await onSend(message.trim(), files);
      setMessage('');
      setFiles([]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    // Typing indicator
    onTyping?.(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => onTyping?.(false), 2000);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
    e.target.value = '';
  };

  // Callback to insert template content
  const handleTemplateSelect = (content) => {
    setMessage(prev => prev ? `${prev}\n\n${content}` : content);
  };

  return (
    <div className="p-3 sm:p-4 border-t bg-white">
      {/* File previews */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {files.map((file, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700 truncate max-w-[100px] sm:max-w-[150px]">{file.name}</span>
              <button 
                onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-1 sm:gap-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          className="hidden"
        />
        
        {/* Template Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onOpenTemplates}
          disabled={disabled}
          className="flex-shrink-0 hidden sm:flex"
          title="Modèles de réponse"
        >
          <FileEdit className="w-5 h-5 text-emerald-600" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="flex-shrink-0"
        >
          <Paperclip className="w-5 h-5 text-gray-500" />
        </Button>
        
        <div className="flex-1 min-w-0">
          <textarea
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Écrivez votre message..."
            disabled={disabled || sending}
            rows={1}
            className="w-full resize-none rounded-xl border border-gray-200 px-3 sm:px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
        </div>

        <Button
          type="submit"
          disabled={(!message.trim() && files.length === 0) || sending || disabled}
          className="flex-shrink-0 bg-emerald-500 hover:bg-emerald-600 rounded-xl h-11 w-11"
        >
          {sending ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </form>
      
      {/* Mobile Template Button */}
      <div className="sm:hidden mt-2 flex justify-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onOpenTemplates}
          disabled={disabled}
          className="text-emerald-600 border-emerald-200"
        >
          <FileEdit className="w-4 h-4 mr-1" />
          Modèles
        </Button>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const MessagingCenterV2 = () => {
  const { user } = useAuth();
  
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [operation, setOperation] = useState(null);
  const [client, setClient] = useState(null);
  const [internalNotes, setInternalNotes] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [showContextPanel, setShowContextPanel] = useState(true);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'chat' | 'context'
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templateContent, setTemplateContent] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('ALL'); // ALL, OPERATION, DIRECT
  const [filterStatus, setFilterStatus] = useState('ALL'); // ALL, OPEN, RESOLVED
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterAssignedToMe, setFilterAssignedToMe] = useState(false);
  const [sortBy, setSortBy] = useState('recent'); // recent, unread
  
  const messagesEndRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const messageInputRef = useRef(null);

  // ============================================================
  // WEBSOCKET CHAT
  // ============================================================

  // RBAC V2 — ops/lead_ops/operateur sont tous des "operator" côté WS
  const _opsSet = new Set(['operateur', 'ops', 'lead_ops']);
  const senderType = user?.role === 'client' ? 'client' : _opsSet.has(user?.role) ? 'operator' : user?.role || 'admin';
  const senderName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : '';

  const {
    isConnected: wsConnected,
    onlineUsers: wsOnlineUsers,
    typingUsers,
    sendMessage: wsSendMessage,
    sendTyping,
    markRead,
    onMessageRef,
    onReadRef,
  } = useChatWebSocket({
    conversationId: selectedConversation?.id,
    userId: user?.id,
    userType: senderType,
    userName: senderName,
    enabled: !!selectedConversation?.id && !!user?.id,
  });

  // Handle incoming WS messages
  useEffect(() => {
    onMessageRef.current = (msg) => {
      // Avoid duplicate: don't add if we just sent it
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      // Update conversation list preview
      setConversations(prev => prev.map(c =>
        c.id === msg.conversationId
          ? { ...c, lastMessageAt: msg.createdAt, lastMessagePreview: msg.content?.slice(0, 100) }
          : c
      ));
    };
  }, [onMessageRef]);

  useEffect(() => {
    onReadRef.current = (messageId, byUserId) => {
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, readBy: [...(m.readBy || []), byUserId], isRead: true } : m
      ));
    };
  }, [onReadRef]);

  // ============================================================
  // DATA FETCHING
  // ============================================================

  const fetchConversations = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (filterType !== 'ALL') params.append('type', filterType);
      if (filterStatus !== 'ALL') params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/conversations?${params}`, {});
      let data = response.data || [];
      
      // Client-side filtering
      if (filterUnread) {
        data = data.filter(c => (c.unreadCountInternal || 0) > 0);
      }
      if (filterAssignedToMe && user?.id) {
        data = data.filter(c => c.assignedAgentId === user.id);
      }
      
      // Sorting
      if (sortBy === 'unread') {
        data.sort((a, b) => {
          const aUnread = a.unreadCountInternal || 0;
          const bUnread = b.unreadCountInternal || 0;
          if (bUnread !== aUnread) return bUnread - aUnread;
          return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
        });
      } else {
        data.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
      }
      
      setConversations(data);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Erreur lors du chargement des conversations');
      setLoading(false);
    }
  }, [filterType, filterStatus, searchQuery, filterUnread, filterAssignedToMe, sortBy, user?.id]);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    if (!token) return;
    
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {});
      setMessages(response.data || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast({
        title: "Erreur",
        description: "Impossible de charger les messages",
        variant: "destructive"
      });
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const fetchOperationDetails = useCallback(async (operationId) => {
    if (!operationId) {
      setOperation(null);
      return;
    }
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/transport-requests/${operationId}`, {});
      setOperation(response.data);
    } catch (err) {
      console.error('Error fetching operation:', err);
      setOperation(null);
    }
  }, []);

  const fetchClientDetails = useCallback(async (clientId) => {
    if (!clientId) {
      setClient(null);
      return;
    }
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/unified-users/${clientId}`, {});
      setClient(response.data);
    } catch (err) {
      console.error('Error fetching client:', err);
      setClient(null);
    }
  }, []);

  const fetchInternalNotes = useCallback(async (conversationId) => {
    if (!conversationId) {
      setInternalNotes([]);
      return;
    }
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/notes`, {});
      setInternalNotes(response.data || []);
    } catch (err) {
      console.error('Error fetching internal notes:', err);
      setInternalNotes([]);
    }
  }, []);

  // ============================================================
  // EFFECTS
  // ============================================================

  // Initial load and polling
  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000); // 10 seconds
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Load messages when conversation changes (initial load only, WS handles updates)
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
    
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      fetchClientDetails(selectedConversation.clientId);
      fetchInternalNotes(selectedConversation.id);
      
      if (selectedConversation.type === 'OPERATION' && selectedConversation.operationId) {
        fetchOperationDetails(selectedConversation.operationId);
      } else {
        setOperation(null);
      }
      
      // Fallback polling only if WS is not connected (slower interval)
      const interval = setInterval(() => {
        if (!wsConnected) {
          fetchMessages(selectedConversation.id);
        }
      }, 8000);
      
      return () => clearInterval(interval);
    } else {
      setMessages([]);
      setOperation(null);
      setClient(null);
      setInternalNotes([]);
    }
  }, [selectedConversation, fetchMessages, fetchOperationDetails, fetchClientDetails, fetchInternalNotes, wsConnected]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setMobileView('chat');
  };

  const handleSendMessage = async (content, files = [], retryOf = null) => {
    if (!selectedConversation) return;
    if (!token) return;

    // Optimistic insert : tmp id + status sending (uniformisé client-side)
    const tmpId = retryOf?.id || `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMsg = {
      id: tmpId,
      conversationId: selectedConversation.id,
      senderId: user?.id,
      senderName: senderName,
      senderType: senderType,
      content,
      attachments: [],
      createdAt: new Date().toISOString(),
      isRead: false,
      readBy: [user?.id],
      status: 'sending',
      sending: true,
      _retryPayload: { content, files },
    };
    if (retryOf) {
      // Remplace le message en erreur par sa version 'sending'
      setMessages(prev => prev.map(m => (m.id === retryOf.id ? optimisticMsg : m)));
    } else {
      setMessages(prev => [...prev, optimisticMsg]);
    }

    // If text-only and WS connected, send via WebSocket (instant)
    if (files.length === 0 && wsConnected) {
      const sent = wsSendMessage(content);
      if (sent) {
        // Le message WS sera echoé via onMessageRef qui dédoublonne par id.
        // On marque le tmp comme 'sent' en attendant l'echo.
        setTimeout(() => {
          setMessages(prev => prev.map(m =>
            m.id === tmpId ? { ...m, status: 'sent', sending: false } : m
          ));
        }, 200);
        fetchConversations();
        return;
      }
      // If WS send failed, fall through to REST
    }

    // REST fallback (also handles file uploads)
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('conversation_id', selectedConversation.id);

      if (selectedConversation.operationId) {
        formData.append('operation_id', selectedConversation.operationId);
      }

      files.forEach(file => formData.append('files', file));

      await axios.post(`${API}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': 'Bearer cookie-auth'
        }
      });

      // On retire le tmp et on recharge la liste pour récupérer le message persisté
      setMessages(prev => prev.filter(m => m.id !== tmpId));
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => prev.map(m =>
        m.id === tmpId
          ? { ...m, status: 'error', error: true, sending: false, _retryPayload: { content, files } }
          : m
      ));
      toast({
        title: "Erreur d'envoi",
        description: 'Cliquez sur "Réessayer" pour renvoyer ce message.',
        variant: 'destructive'
      });
    }
  };

  const handleRetry = (msg) => {
    if (!msg) return;
    const payload = msg._retryPayload || { content: msg.content || '', files: [] };
    handleSendMessage(payload.content, payload.files || [], msg);
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedConversation) return;
    if (!token) return;
    
    try {
      await axios.put(`${API}/conversations/${selectedConversation.id}/status?status=${status}`, null, {});
      setSelectedConversation(prev => prev ? { ...prev, status } : null);
      fetchConversations();
      toast({
        title: "Statut mis à jour",
        description: `Conversation ${status === 'RESOLVED' ? 'résolue' : 'réouverte'}`
      });
    } catch (err) {
      console.error('Error updating status:', err);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive"
      });
    }
  };

  // Handler for priority change
  const handleUpdatePriority = async (priority) => {
    if (!selectedConversation) return;
    if (!token) return;
    
    try {
      await axios.put(`${API}/conversations/${selectedConversation.id}/priority`, 
        { priority },
        {}
      );
      setSelectedConversation(prev => prev ? { ...prev, priority } : null);
      fetchConversations();
      toast({
        title: "Priorité mise à jour",
        description: `Priorité définie sur ${PRIORITY_LEVELS[priority]?.label || priority}`
      });
    } catch (err) {
      console.error('Error updating priority:', err);
      // Update locally anyway for UX
      setSelectedConversation(prev => prev ? { ...prev, priority } : null);
    }
  };

  // Handler for adding internal note
  const handleAddInternalNote = async (noteData) => {
    if (!selectedConversation) return;
    if (!token) return;
    
    try {
      const response = await axios.post(
        `${API}/conversations/${selectedConversation.id}/notes`,
        noteData,
        {}
      );
      setInternalNotes(prev => [...prev, response.data]);
      toast({ title: "Note ajoutée" });
    } catch (err) {
      console.error('Error adding note:', err);
      // Add locally for UX
      const localNote = {
        id: `local-${Date.now()}`,
        ...noteData
      };
      setInternalNotes(prev => [...prev, localNote]);
    }
  };

  // Handler for deleting internal note
  const handleDeleteInternalNote = async (noteId) => {
    if (!selectedConversation) return;
    try {
      await axios.delete(
        `${API}/conversations/${selectedConversation.id}/notes/${noteId}`,
        {}
      );
      setInternalNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      // Delete locally anyway
      setInternalNotes(prev => prev.filter(n => n.id !== noteId));
    }
  };

  // Handler for template selection
  const handleTemplateSelect = (content) => {
    setTemplateContent(content);
    setShowTemplatesModal(false);
  };

  // ============================================================
  // COMPUTED VALUES
  // ============================================================

  const filteredConversations = useMemo(() => {
    return conversations;
  }, [conversations]);

  const conversationCounts = useMemo(() => {
    const all = conversations.length;
    const operations = conversations.filter(c => c.type === 'OPERATION').length;
    const direct = conversations.filter(c => c.type === 'DIRECT').length;
    const unread = conversations.filter(c => (c.unreadCountInternal || 0) > 0).length;
    return { all, operations, direct, unread };
  }, [conversations]);

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentDate = null;
    
    messages.forEach((msg) => {
      const msgDate = new Date(msg.createdAt).toDateString();
      const showDate = msgDate !== currentDate;
      if (showDate) currentDate = msgDate;
      groups.push({ ...msg, showDate });
    });
    
    return groups;
  }, [messages]);

  // 1er message non lu reçu (pour séparateur "Nouveaux messages")
  const firstUnreadMsgId = useMemo(() => {
    if (!user?.id) return null;
    for (const m of messages) {
      const isMine = m.senderId === user.id;
      if (isMine) continue;
      const readBy = Array.isArray(m.readBy) ? m.readBy : [];
      if (!readBy.includes(user.id) && !m.isRead) {
        return m.id || m._id;
      }
    }
    return null;
  }, [messages, user?.id]);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="h-[calc(100vh-64px)] flex bg-gray-100" data-testid="messaging-center-v2">
      {/* LEFT PANEL - Conversation List */}
      <div className={`
        w-full md:w-[360px] lg:w-[380px] flex-shrink-0 bg-white border-r border-gray-200 flex flex-col
        ${mobileView !== 'list' ? 'hidden md:flex' : 'flex'}
      `}>
        {/* Header */}
        <div className="p-4 border-b bg-emerald-600">
          <h1 className="text-xl font-bold text-white mb-3">Messagerie</h1>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-200" />
            <Input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-emerald-500/50 border-emerald-400 text-white placeholder:text-emerald-200 focus:bg-white focus:text-gray-900 focus:placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {[
            { key: 'ALL', label: 'Toutes', count: conversationCounts.all },
            { key: 'OPERATION', label: 'Opérations', count: conversationCounts.operations },
            { key: 'DIRECT', label: 'Directes', count: conversationCounts.direct }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterType(tab.key)}
              className={`
                flex-1 py-2.5 text-sm font-medium transition-colors relative
                ${filterType === tab.key 
                  ? 'text-emerald-600 border-b-2 border-emerald-600' 
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              {tab.label}
              <span className="ml-1 text-xs text-gray-400">({tab.count})</span>
            </button>
          ))}
        </div>

        {/* Filters Row */}
        <div className="p-2 border-b flex items-center gap-2 flex-wrap">
          <Button
            variant={filterUnread ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterUnread(!filterUnread)}
            className={`text-xs h-7 ${filterUnread ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
          >
            <Circle className={`w-2 h-2 mr-1 ${filterUnread ? 'fill-white' : 'fill-emerald-500 text-emerald-500'}`} />
            Non lues ({conversationCounts.unread})
          </Button>
          <Button
            variant={filterAssignedToMe ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterAssignedToMe(!filterAssignedToMe)}
            className={`text-xs h-7 ${filterAssignedToMe ? 'bg-purple-500 hover:bg-purple-600' : ''}`}
          >
            <UserCheck className="w-3 h-3 mr-1" />
            Mes conversations
          </Button>
          <Button
            variant={filterStatus !== 'ALL' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(filterStatus === 'ALL' ? 'OPEN' : filterStatus === 'OPEN' ? 'RESOLVED' : 'ALL')}
            className={`text-xs h-7 ${filterStatus !== 'ALL' ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
          >
            <Filter className="w-3 h-3 mr-1" />
            {filterStatus === 'ALL' ? 'Statut' : getStatusLabel(filterStatus)}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortBy(sortBy === 'recent' ? 'unread' : 'recent')}
            className="text-xs h-7 ml-auto"
          >
            <SortDesc className="w-3 h-3 mr-1" />
            {sortBy === 'recent' ? 'Récent' : 'Non lues'}
          </Button>
        </div>

        {/* Conversation List */}
        <ScrollArea className="flex-1">
          {loading ? (
            Array(5).fill(0).map((_, i) => <ConversationSkeleton key={i} />)
          ) : error ? (
            <div className="p-6 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
              <p className="text-red-600 mb-3">{error}</p>
              <Button variant="outline" onClick={fetchConversations}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Réessayer
              </Button>
            </div>
          ) : filteredConversations.length === 0 ? (
            <EmptyConversationList onRefresh={fetchConversations} />
          ) : (
            filteredConversations.map(conv => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isSelected={selectedConversation?.id === conv.id}
                onClick={() => handleSelectConversation(conv)}
                currentUserId={user?.id}
              />
            ))
          )}
        </ScrollArea>
      </div>

      {/* CENTER PANEL - Chat */}
      <div className={`
        flex-1 flex flex-col bg-gray-50 min-w-0
        ${mobileView !== 'chat' ? 'hidden md:flex' : 'flex'}
      `}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="px-3 sm:px-4 py-3 bg-white border-b flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden flex-shrink-0"
                  onClick={() => setMobileView('list')}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <Avatar className="h-9 w-9 sm:h-10 sm:w-10 bg-emerald-500 flex-shrink-0">
                  <AvatarFallback className="text-white text-sm">
                    {getInitials(selectedConversation.clientName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                    {selectedConversation.clientName || 'Client'}
                  </h3>
                  <div className="flex items-center gap-1 sm:gap-2 text-xs text-gray-500 flex-wrap">
                    {/* Online / WS status */}
                    {wsConnected ? (
                      <span className="flex items-center gap-1 text-emerald-600" data-testid="ws-connected-badge">
                        <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                        <span className="hidden sm:inline">En ligne</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400" data-testid="ws-disconnected-badge">
                        <Circle className="w-2 h-2 fill-gray-300 text-gray-300" />
                        <span className="hidden sm:inline">Hors ligne</span>
                      </span>
                    )}
                    {selectedConversation.operationReference && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Package className="w-3 h-3" />
                        <span className="hidden sm:inline">{selectedConversation.operationReference}</span>
                        <span className="sm:hidden">{selectedConversation.operationReference.slice(-8)}</span>
                      </span>
                    )}
                    <Badge className={`text-[10px] ${getStatusColor(selectedConversation.status)}`}>
                      {getStatusLabel(selectedConversation.status)}
                    </Badge>
                    {/* Priority Badge */}
                    {selectedConversation.priority && selectedConversation.priority !== 'NORMAL' && (
                      <PriorityBadge priority={selectedConversation.priority} size="small" />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Priority Selector - hidden on mobile */}
                <div className="hidden sm:block">
                  <PrioritySelector
                    currentPriority={selectedConversation.priority || 'NORMAL'}
                    onPriorityChange={handleUpdatePriority}
                    size="small"
                    showLabel={false}
                  />
                </div>
                {selectedConversation.status === 'OPEN' ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleUpdateStatus('RESOLVED')}
                    className="text-green-600 border-green-200 hover:bg-green-50 text-xs sm:text-sm"
                  >
                    <Check className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Résoudre</span>
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleUpdateStatus('OPEN')}
                    className="text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">Réouvrir</span>
                    <span className="sm:hidden">↻</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowContextPanel(!showContextPanel)}
                  className="hidden lg:flex"
                >
                  {showContextPanel ? <PanelRightClose className="w-5 h-5" /> : <PanelRightOpen className="w-5 h-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileView('context')}
                  className="lg:hidden"
                >
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun message dans cette conversation</p>
                  </div>
                </div>
              ) : (
                <>
                  {groupedMessages.map((msg) => {
                    // Improved logic: Compare sender ID with current user ID
                    // Fallback to senderType check if senderId is not available
                    const isOwnMessage = msg.senderId 
                      ? msg.senderId === user?.id 
                      : msg.senderType !== 'client';
                    const msgId = msg.id || msg._id;
                    const showNewSeparator = firstUnreadMsgId && msgId === firstUnreadMsgId;

                    return (
                      <React.Fragment key={msgId}>
                        {showNewSeparator && (
                          <div
                            className="flex items-center gap-3 my-4 px-1"
                            data-testid="new-messages-separator"
                            role="separator"
                            aria-label="Nouveaux messages"
                          >
                            <div className="flex-1 h-px bg-emerald-200" />
                            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider rounded-full border border-emerald-200 shadow-sm">
                              Nouveaux messages
                            </span>
                            <div className="flex-1 h-px bg-emerald-200" />
                          </div>
                        )}
                        <MessageBubble
                          message={msg}
                          isOwn={isOwnMessage}
                          showDate={msg.showDate}
                          currentUserId={user?.id}
                          onRetry={handleRetry}
                        />
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </ScrollArea>

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
              <div className="px-4 py-1.5 text-xs text-gray-500 flex items-center gap-2" data-testid="typing-indicator">
                <span className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{animationDelay: '0ms'}} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{animationDelay: '150ms'}} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{animationDelay: '300ms'}} />
                </span>
                Quelqu&apos;un est en train d&apos;ecrire...
              </div>
            )}

            {/* Internal Notes - Staff Only */}
            {user?.role && user.role !== 'client' && (
              <div className="px-4 py-2 border-t bg-white">
                <InternalNotes
                  notes={internalNotes}
                  onAddNote={handleAddInternalNote}
                  onDeleteNote={handleDeleteInternalNote}
                  currentUser={user}
                  conversationId={selectedConversation?.id}
                />
              </div>
            )}

            {/* Message Input */}
            <MessageInput 
              onSend={handleSendMessage}
              disabled={selectedConversation.status === 'RESOLVED'}
              onOpenTemplates={() => setShowTemplatesModal(true)}
              onTyping={sendTyping}
              initialContent={templateContent}
            />
          </>
        ) : (
          <EmptyChat />
        )}
      </div>

      {/* RIGHT PANEL - Context */}
      {showContextPanel && (
        <div className={`
          w-[320px] flex-shrink-0 border-l border-gray-200 bg-white
          ${mobileView !== 'context' ? 'hidden lg:block' : 'block fixed inset-0 z-50 lg:relative lg:inset-auto'}
        `}>
          <ContextPanel
            conversation={selectedConversation}
            operation={operation}
            client={client}
            onClose={() => {
              setShowContextPanel(false);
              setMobileView('chat');
            }}
          />
        </div>
      )}

      {/* Response Templates Modal */}
      <ResponseTemplatesModal
        isOpen={showTemplatesModal}
        onClose={() => setShowTemplatesModal(false)}
        onSelect={handleTemplateSelect}
      />
    </div>
  );
};

export default MessagingCenterV2;
